import { useEffect } from 'react';
import { GetStaticProps } from 'next';
import NotFound from 'src/NotFound';
import Layout from 'src/Layout';
import { SitecoreContext, ComponentPropsContext, Field, Item, getFieldValue } from '@sitecore-jss/sitecore-jss-nextjs';
import { handleEditorFastRefresh } from '@sitecore-jss/sitecore-jss-nextjs/utils';
import { SitecorePageProps } from 'lib/page-props';
import { sitecorePagePropsFactory } from 'lib/page-props-factory';
import { componentBuilder } from 'temp/componentBuilder';
import { purgeNextjsStaticFiles } from 'lib/purge-files';
import { getSlugsFromPageContext } from 'lib/utils';

type GetFieldValueFields = {
  [name: string]: Field | Item[];
};

const SitecorePage = (props: SitecorePageProps): React.JSX.Element => {
  const { notFound, componentProps, layoutData, appUrl, headLinks } = props;

  useEffect(() => {
    // Since Sitecore editors do not support Fast Refresh, need to refresh editor chromes after Fast Refresh finished
    handleEditorFastRefresh();
  }, []);

  if (notFound || !layoutData.sitecore.route) {
    // Shouldn't hit this (as long as 'notFound' is being returned below), but just to be safe
    return <NotFound />;
  }

  const { route } = layoutData.sitecore;
  const fields = route?.fields as GetFieldValueFields;
  const isEditing = layoutData.sitecore.context.pageEditing;
  const bodyScripts: string = getFieldValue(fields, 'bodyScripts') || '';

  return (
    <ComponentPropsContext value={componentProps}>
      <SitecoreContext componentFactory={componentBuilder.getComponentFactory({ isEditing })} layoutData={layoutData}>
        <>
          <Layout layoutData={layoutData} appUrl={appUrl} headLinks={headLinks} />
          <span dangerouslySetInnerHTML={{ __html: bodyScripts }} />
        </>
      </SitecoreContext>
    </ComponentPropsContext>
  );
};

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// revalidation (or fallback) is enabled and a new request comes in.
export const getServerSideProps: GetStaticProps = async (context) => {
  const props = await sitecorePagePropsFactory.create(context);
  props.appUrl = props.appUrl || process.env['PUBLIC_URL'] || '';

  // if the page isn't found in CMS, purge the next.js static files (fix for issue: https://github.com/vercel/next.js/issues/37945)
  // Sitecore support case: https://support.sitecore.com/csm?id=csm_ticket&table=sn_customerservice_case&sys_id=d05c7aa41beef59038a46421b24bcbb7&view=sp
  if (props.notFound) {
    const slugs = getSlugsFromPageContext(context);
    await purgeNextjsStaticFiles(slugs);
  }

  return {
    props,
    notFound: props.notFound, // Returns custom 404 page with a status code of 404 when true
  };
};

export default SitecorePage;
